import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import * as actions from "../../../store/actions";
import { i18n } from '../../../translate/i18n';
import {
  Paper, Avatar, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography,
} from '@mui/material';
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';

import { formatLanguageFromLocalStorage } from '../../../utils/formatLanguageFromLocalStorage';
import { EAccessRole } from "../../../store/types";

export default function TableEmpresas(props) {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [exportLoading, setExportLoading] = useState(false);

  const localStorageLang = localStorage.getItem("i18nextLng");
  const emailLanguage = formatLanguageFromLocalStorage(localStorageLang);
  
  const fetchCompaniesExport = useCallback(() => dispatch(actions.fetchCompaniesExport(searchText, order, orderBy, emailLanguage)), [searchText, order, orderBy, emailLanguage]);
    
  const fetchCompanies = useCallback(
    () => dispatch(actions.fetchCompanies(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );
  
  const companies = useSelector(state => state.companies.companies);
  const companiesFilter = useSelector(state => state.companies.companiesFilter);
  const loading = useSelector(state => state.companies.loading);
  const selectedAccess = useSelector(state => state.auth.selectedAccess);

  const isAnalyst = selectedAccess.role === EAccessRole.analyst;
  const isInspector = selectedAccess.inspector;

  const headRows = [
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsCompany.name') },
    { id: "email", numeric: false, disablePadding: false, label: i18n.t('headRowsCompany.email') },
    { id: "accountable", numeric: false, disablePadding: false, label: i18n.t('headRowsCompany.accountable') },
  ];

  useEffect(() => {
    if (!isEmpty(companiesFilter)) {
      setPage(companiesFilter.page);
      setLimit(companiesFilter.limit);
      setSearchText(companiesFilter.searchText);
      setOrderBy(companiesFilter.orderBy);
      setOrder(companiesFilter.order);
    }
  }, []);

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  useEffect(() => {
    fetchCompanies(page, limit, searchText, order, orderBy);
    setAlreadySerched(true);
  }, [page, limit, order, orderBy, searchText]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchCompanies(page, limit, searchText, order, orderBy);
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const selectRow = (e, id) => {
    props.history.push(`form-empresa/${id}`)
  }

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    tableRow: {
      cursor: 'pointer',
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    thumbUp: {
      marginRight: 15,
      color: "#349c34"
    },
    thumbDown: {
      marginRight: 15,
      color: "#d04545"
    },
    avatar: {
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    companyName: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '0.75rem',
    },
    avatarSmallText: {
      fontSize: '0.75rem',
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    chip: {
      margin: 5,
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    cell: {
      fontSize: '0.775rem',
      padding: '0.25rem',
    },
  }

  const exportExcel = () => {
    setExportLoading(true);
    fetchCompaniesExport().then(response => {
      if (response) {
        window.open(response.url, "_blank")
      }
      setExportLoading(false);
    })
  }

  return <>
    <Container maxWidth="xl">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleCompany.title')}
          placeholder={i18n.t('tableTitleCompany.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          addRow={() => props.history.push(`/form-empresa/new`)}
          clickExport={exportExcel}
          downloading={exportLoading}
          hideAddButton={isAnalyst && isInspector}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {companies.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('tableTitleCompany.Typography')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={styles.opacityContainer}>
            {companies.rows.map((col, index) => (
              <TableRow
                style={styles.tableRow}
                key={col._id}
                hover
                onClick={event => selectRow(event, col._id)}
              >
                <TableCell style={styles.cell}>
                  <div style={styles.avatarContainer}>
                    {col.photo ? <Avatar src={col.photo.url} style={styles.avatar} /> : <Avatar style={styles.avatarSmallText}>{i18n.t('tableCellUser.photo')}</Avatar>}
                    <div style={styles.companyName}>
                      <div>{col.name}</div>
                      <div style={{ color: 'grey' }}>{col.tradingName}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell style={styles.cell}>{col.email}</TableCell>
                <TableCell style={styles.cell}>{col.accountable}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={companies.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;
}
